import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';
import Cookies from 'js-cookie';

interface RegistrationScannerScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

interface RegistrationData {
  title_indication?: string | null;
  description_indication?: string | null;
  save_button_indication?: string | null;
  save_button_bg_color?: string | null;
  save_button_text_color?: string | null;
  save_button_shadow_color?: string | null;
  show_success_modal: boolean;
  fields: {
    name: string;
    key: string;
    field_type: string;
    required: boolean;
    options?: string[];
  }[];
}

interface DynScannerInstance {
  launch(): Promise<{
    barcodeResults?: Array<{ text?: string; barcodeText?: string }>;
  }>;
  dispose?(): Promise<void>;
}

interface DynNS {
  BarcodeScanner: new (opts: Record<string, unknown>) => DynScannerInstance;
  EnumBarcodeFormat?: { BF_CODE_128?: unknown; BF_CODE_39?: unknown };
  EnumScanMode?: { SM_SINGLE?: unknown };
  engineResourcePaths?: { rootDirectory?: string; dbrBundle?: string };
}

const ENV = (import.meta as { env?: Record<string, string> }).env ?? {};
const LICENSE = ENV.VITE_DBR_LICENSE || '';
const STATIC_URL = (ENV.VITE_STATIC_URL || '').replace(/\/$/, '');
const SDK_DIR = 'assets/dbr/dynamsoft-barcode-reader-js-11.0.6000.250916/dist';

export default function RegistrationScannerScreen({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}: RegistrationScannerScreenProps) {
  const {
    backendBaseUrl,
    registerUser,
    event,
    setGlobalNotification,
    setUserEmail,
    associatePendingFeedbacks,
    style,
  } = useAppContext();
  const [registrationData, setRegistrationData] =
    useState<RegistrationData | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const eventId = event?.id;

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'ready' | 'scanning' | 'posting'
  >('idle');
  const [error, setError] = useState<string>('');
  const [bundleRoot, setBundleRoot] = useState<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scannerRef = useRef<DynScannerInstance | null>(null);
  const dynRef = useRef<DynNS | null>(null);
  const overlayOpenRef = useRef(false);
  const sentRef = useRef(false);
  const autoStartRef = useRef(false);

  useEffect(() => {
    if (event?.remember_registration) {
      const rememberedEmail = Cookies.get('registeredEmail');
      if (rememberedEmail) {
        setUserEmail(rememberedEmail);
        return;
      }
    } else {
      Cookies.remove('registeredEmail');
    }
  }, [event?.remember_registration, setUserEmail]);

  useEffect(() => {
    if (eventId) {
      axios
        .get(`${backendBaseUrl}/get-registration/?event_id=${eventId}`)
        .then(res => {
          setRegistrationData(res.data);
        })
        .catch(() => {});
    }
  }, [eventId, backendBaseUrl]);

  const isRealBundle = useCallback(async (url: string) => {
    try {
      const r = await fetch(url, { method: 'GET' });
      if (!r.ok) return false;
      const ct = r.headers.get('content-type') || '';
      const text = await r.text();
      if (ct.includes('javascript')) return true;
      return text.includes('Dynamsoft') && text.length > 50000;
    } catch {
      return false;
    }
  }, []);

  const probeRoots = useCallback(async () => {
    const origin = window.location.origin;
    // Try the path that matches the current deployment FIRST, then fall back.
    // - With STATIC_URL set (production): assets live under e.g. /static/dist/.
    // - Without it (dev, Vite base '/'): public assets are served at the root.
    const cands = STATIC_URL
      ? [
          `${origin}${STATIC_URL}/${SDK_DIR}`,
          `${origin}/${SDK_DIR}`,
        ]
      : [
          `${origin}/${SDK_DIR}`,
          `${origin}/static/dist/${SDK_DIR}`,
          `${origin}/static/${SDK_DIR}`,
          `${origin}/dist/${SDK_DIR}`,
        ];
    for (const root of cands) {
      const ok = await isRealBundle(`${root}/dbr.bundle.js`);
      if (ok) {
        setBundleRoot(root);
        return;
      }
    }
    setBundleRoot('');
  }, [isRealBundle]);

  useEffect(() => {
    probeRoots();
  }, [probeRoots]);

  const lazyImportBundle = useCallback(async (): Promise<DynNS> => {
    const w = window as Window & { Dynamsoft?: DynNS };
    if (!bundleRoot) throw new Error('Nessun bundleRoot valido trovato');
    const inject = (src: string) =>
      new Promise<boolean>(resolve => {
        if (document.querySelector(`script[src="${src}"]`))
          return resolve(true);
        const s = document.createElement('script');
        s.src = src;
        s.defer = true;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.head.appendChild(s);
      });
    const src = `${bundleRoot}/dbr.bundle.js`;
    const ok = await inject(src);
    const hasScanner = !!w?.Dynamsoft?.BarcodeScanner;
    if (!ok || !hasScanner)
      throw new Error('Bundle v11 non trovato o BarcodeScanner non esposto');
    w.Dynamsoft.engineResourcePaths = {
      rootDirectory: bundleRoot,
      dbrBundle: bundleRoot,
    };
    return w.Dynamsoft;
  }, [bundleRoot]);

  const stop = useCallback(async () => {
    try {
      setStatus('loading');
    } catch {
      /* ignored */
    }
    try {
      sentRef.current = false;
    } catch {
      /* ignored */
    }
    try {
      await scannerRef.current?.dispose?.();
    } catch {
      /* ignored */
    }
    scannerRef.current = null;
    overlayOpenRef.current = false;
    setStatus('idle');
  }, []);

  useEffect(
    () => () => {
      if (overlayOpenRef.current) stop();
    },
    [stop]
  );

  const handleChange = useCallback((key: string, value: string) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (registrationData && registrationData.fields) {
      registrationData.fields.forEach(field => {
        const value = formValues[field.key];
        if (field.required && !value) {
          newErrors[field.key] = `${field.name} is required.`;
        } else if (
          field.field_type === 'email' &&
          value &&
          !value.includes('@')
        ) {
          newErrors[field.key] = 'Invalid email address.';
        } else if (
          field.field_type === 'int' &&
          value &&
          isNaN(Number(value))
        ) {
          newErrors[field.key] = 'Must be a number.';
        } else if (
          field.field_type === 'phone' &&
          value &&
          !/^[+]?^[1-9][\d]{0,15}$/.test(value.replace(/[\s\-()]/g, ''))
        ) {
          newErrors[field.key] = 'Invalid phone number.';
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [registrationData, formValues]);

  const handleSubmit = useCallback(async () => {
    if (!validate() || !eventId) return;
    try {
      const res = await registerUser(formValues, eventId);
      if (res.success) {
        const email = formValues.email_address || formValues.mail;
        const username = formValues.username;
        const identifier = email || username;

        if (event?.remember_registration && email) {
          Cookies.set('registeredEmail', email, { expires: 365 });
        }
        setUserEmail(identifier || '');

        if (identifier) {
          associatePendingFeedbacks(identifier);
        }
        if (registrationData?.show_success_modal) {
          setGlobalNotification(['Registration successful!', 'success']);
        }
        onNext();
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setSubmitError(err.response?.data?.error || 'Registration failed');
    }
  }, [
    validate,
    eventId,
    registerUser,
    formValues,
    event?.remember_registration,
    setUserEmail,
    associatePendingFeedbacks,
    registrationData,
    setGlobalNotification,
    onNext,
  ]);

  const onScanComplete = useCallback(
    async (scannedText: string) => {
      if (!registrationData || registrationData.fields.length !== 1) return;

      const fieldKey = registrationData.fields[0].key;
      const updatedFormValues = { ...formValues, [fieldKey]: scannedText };
      setFormValues(updatedFormValues);

      stop();

      setTimeout(async () => {
        const newErrors: Record<string, string> = {};
        if (registrationData && registrationData.fields) {
          registrationData.fields.forEach(field => {
            const value = updatedFormValues[field.key];
            if (field.required && !value) {
              newErrors[field.key] = `${field.name} is required.`;
            } else if (
              field.field_type === 'email' &&
              value &&
              !value.includes('@')
            ) {
              newErrors[field.key] = 'Invalid email address.';
            } else if (
              field.field_type === 'int' &&
              value &&
              isNaN(Number(value))
            ) {
              newErrors[field.key] = 'Must be a number.';
            } else if (
              field.field_type === 'phone' &&
              value &&
              !/^[+]?^[1-9][\d]{0,15}$/.test(value.replace(/[\s\-()]/g, ''))
            ) {
              newErrors[field.key] = 'Invalid phone number.';
            }
          });
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0 && eventId) {
          try {
            const res = await registerUser(updatedFormValues, eventId);
            if (res.success) {
              const email =
                updatedFormValues.email_address || updatedFormValues.mail;
              const username = updatedFormValues.username;
              const identifier = email || username;

              if (event?.remember_registration && email) {
                Cookies.set('registeredEmail', email, { expires: 365 });
              }
              setUserEmail(identifier || '');

              if (identifier) {
                associatePendingFeedbacks(identifier);
              }
              if (registrationData?.show_success_modal) {
                setGlobalNotification(['Registration successful!', 'success']);
              }
              onNext();
            }
          } catch (e: unknown) {
            const err = e as { response?: { data?: { error?: string } } };
            setSubmitError(err.response?.data?.error || 'Registration failed');
          }
        }
      }, 100);
    },
    [
      registrationData,
      formValues,
      stop,
      eventId,
      registerUser,
      event?.remember_registration,
      setUserEmail,
      associatePendingFeedbacks,
      setGlobalNotification,
      onNext,
    ]
  );

  const start = useCallback(async () => {
    try {
      setError('');
      setStatus('loading');
      overlayOpenRef.current = true;
      const Dyn = await lazyImportBundle();
      dynRef.current = Dyn;
      const Scanner = Dyn?.BarcodeScanner;
      if (!Scanner)
        throw new Error('BarcodeScanner non disponibile nel bundle');
      const f128 = Dyn.EnumBarcodeFormat?.BF_CODE_128;
      const f39 = Dyn.EnumBarcodeFormat?.BF_CODE_39;
      const formats = f128 && f39 ? [f128, f39] : undefined;
      const scanner = new Scanner({
        license: LICENSE || '',
        barcodeFormats: formats,
        container: containerRef.current || undefined,
        autoStartCapturing: true,
        scanMode: Dyn.EnumScanMode?.SM_SINGLE,
      });
      scannerRef.current = scanner;
      setStatus('scanning');
      const result = await scanner.launch();
      const item = result?.barcodeResults?.[0];
      const text = item?.text || item?.barcodeText || '';
      if (text) {
        await onScanComplete(text);
      } else {
        setStatus('ready');
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Impossibile avviare lo scanner');
      setStatus('ready');
    }
  }, [lazyImportBundle, onScanComplete]);

  useEffect(() => {
    if (
      registrationData?.fields?.length === 1 &&
      bundleRoot &&
      !autoStartRef.current
    ) {
      autoStartRef.current = true;
      start();
    }
  }, [registrationData, bundleRoot, start]);

  useEffect(() => {
    setStatus('ready');
    return () => {
      if (overlayOpenRef.current) stop();
    };
  }, [stop]);

  if (!registrationData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        Loading...
      </div>
    );
  }

  if (registrationData.fields.length !== 1) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        This screen requires exactly 1 registration field.
      </div>
    );
  }

  const singleField = registrationData.fields[0];
  const btnBg =
    registrationData?.save_button_bg_color ||
    'linear-gradient(to right, #6991F8 0%, #07296a 100%)';
  const btnText = registrationData?.save_button_text_color || 'white';
  const btnShadow = registrationData?.save_button_shadow_color || '#6991F8';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'clamp(8px, 1.5vh, 16px)',
        padding: 'clamp(8px, 2vw, 20px)',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {registrationData?.title_indication && (
        <div
          style={{ textAlign: 'center', fontWeight: 600 }}
          dangerouslySetInnerHTML={{
            __html: registrationData.title_indication,
          }}
        />
      )}
      {registrationData?.description_indication && (
        <div
          style={{ textAlign: 'center' }}
          dangerouslySetInnerHTML={{
            __html: registrationData.description_indication,
          }}
        />
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          maxWidth: 720,
          height: 'clamp(220px, 45vh, 480px)',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#000',
        }}
      />

      {error && (
        <div
          style={{
            backgroundColor: '#ffe6e6',
            border: '2px solid #ff4444',
            borderRadius: 8,
            padding: 16,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
          }}
        >
          <div
            style={{ marginBottom: 8 }}
            dangerouslySetInnerHTML={{ __html: error }}
          />
          <button
            onClick={() => setError('')}
            style={{
              backgroundColor: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        </div>
      )}

      {submitError && (
        <div
          style={{ color: 'red', textAlign: 'center', marginBottom: '15px' }}
        >
          {submitError}
        </div>
      )}

      {formValues[singleField.key] && (
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            padding: '12px',
            background: '#f0f0f0',
            borderRadius: 8,
          }}
        >
          <label style={{ display: 'block', marginBottom: '5px' }}>
            {singleField.name}
            {singleField.required ? ' *' : ''}
          </label>
          <input
            type="text"
            value={formValues[singleField.key] || ''}
            onChange={e => handleChange(singleField.key, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 4,
              border: '1px solid #ccc',
              fontSize: '14px',
            }}
          />
          {errors[singleField.key] && (
            <div style={{ color: 'red', fontSize: '0.9em', marginTop: '5px' }}>
              {errors[singleField.key]}
            </div>
          )}
        </div>
      )}

      {status !== 'scanning' && status !== 'loading' && (
        <button
          onClick={handleSubmit}
          disabled={!formValues[singleField.key]}
          style={{
            height: '50px',
            minHeight: '44px',
            marginTop: '15px',
            width: '100%',
            maxWidth: '400px',
            borderRadius: '7px',
            padding: '0 20px',
            fontSize: '90%',
            fontWeight: 'bold',
            color: btnText,
            background: btnBg,
            border: 'none',
            cursor: formValues[singleField.key] ? 'pointer' : 'not-allowed',
            opacity: formValues[singleField.key] ? 1 : 0.5,
            boxShadow: `0 5px 5px ${btnShadow}`,
          }}
        >
          {registrationData?.save_button_indication && (
            <span
              style={{
                display: 'inline-block',
                width: '100%',
                textAlign: 'center',
              }}
              dangerouslySetInnerHTML={{
                __html: registrationData.save_button_indication,
              }}
            />
          )}
        </button>
      )}
      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}
      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={onNext}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
}
